import React from 'react';
import {
  Box,
  Button,
  Menu,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  InputAdornment,
  useTheme,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { MinimumOrderQuantityType } from '@infrastructure/suppliers/dto/SupplierDTO';
import { coerceMoqToItemsOnTypeSwitch, sanitizeMoqItemsInput } from './supplierLinkFormUtils';

const menuAnchorProps = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'right' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
};

const currencySymbol = (code: string) => {
  switch (code) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    case 'CHF':
      return 'Fr.';
    default:
      return code;
  }
};

export interface MinimumOrderQuantityMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Menu title (matches product catalog bulk dropdowns) */
  title?: string;
  /** ISO currency code from supplier link form (e.g. EUR) — used for MOQ type “currency” */
  currencyCode: string;
  minimumOrderQuantity: string;
  minimumOrderQuantityType: MinimumOrderQuantityType | '';
  orderInMultiplesOf: string;
  onChange: (patch: {
    minimumOrderQuantity?: string;
    minimumOrderQuantityType?: MinimumOrderQuantityType | '';
    orderInMultiplesOf?: string;
  }) => void;
}

const MinimumOrderQuantityMenu: React.FC<MinimumOrderQuantityMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onConfirm,
  title = 'MOQ',
  currencyCode,
  minimumOrderQuantity,
  minimumOrderQuantityType,
  orderInMultiplesOf,
  onChange,
}) => {
  const theme = useTheme();
  const moqType: MinimumOrderQuantityType = minimumOrderQuantityType || 'items';
  const sym = currencySymbol(currencyCode || 'EUR');

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      disableAutoFocusItem
      {...menuAnchorProps}
      PaperProps={{
        sx: { width: 320, maxWidth: 'calc(100vw - 24px)', mt: 0.5, overflow: 'hidden' },
      }}
    >
      <Box sx={{ p: 0 }} onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <Typography variant="subtitle2">{title}</Typography>
        </DialogHeader>

        <Stack spacing={2} px={3} py={2}>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.primary">
              Minimum order quantity
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={moqType}
                onChange={(_, value: MinimumOrderQuantityType | null) => {
                  if (!value) return;
                  if (value === 'items') {
                    onChange({
                      minimumOrderQuantityType: value,
                      minimumOrderQuantity: coerceMoqToItemsOnTypeSwitch(minimumOrderQuantity),
                    });
                  } else {
                    onChange({ minimumOrderQuantityType: value });
                  }
                }}
                aria-label="MOQ unit"
              >
                <ToggleButton value="items" aria-label="Items">
                  <Stack direction="row" alignItems="center" gap={0.5} component="span">
                    <Typography variant="caption" component="span">
                      Items
                    </Typography>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="currency" aria-label="Euro or currency amount">
                  <Stack direction="row" alignItems="center" gap={0.5} component="span">
                    <Typography variant="caption" component="span">
                      {currencyCode || 'EUR'}
                    </Typography>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                type="number"
                value={minimumOrderQuantity}
                onChange={e => {
                  const raw = e.target.value;
                  onChange({
                    minimumOrderQuantity: moqType === 'items' ? sanitizeMoqItemsInput(raw) : raw,
                  });
                }}
                inputProps={{
                  step: moqType === 'currency' ? 0.01 : 1,
                  min: 0,
                  ...(moqType === 'items' ? { inputMode: 'numeric' as const } : {}),
                }}
                placeholder="0"
                sx={{ flex: 1, minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                InputProps={
                  moqType === 'currency'
                    ? {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" color="text.secondary">
                              {sym}
                            </Typography>
                          </InputAdornment>
                        ),
                      }
                    : {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" color="text.secondary">
                              items
                            </Typography>
                          </InputAdornment>
                        ),
                      }
                }
              />
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.primary">
              Order in multiples of
            </Typography>
            <TextField
              size="small"
              type="number"
              value={orderInMultiplesOf}
              onChange={e => onChange({ orderInMultiplesOf: e.target.value })}
              inputProps={{ step: 1, min: 0 }}
              placeholder="1"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2, borderTop: 1, borderColor: theme.palette.divider }}
        >
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onConfirm}>
            Apply
          </Button>
        </Stack>
      </Box>
    </Menu>
  );
};

export default MinimumOrderQuantityMenu;
